// 範囲指定（マーキー）選択ハンドラ。
//
// 振る舞い:
// - 右ボタン（副ボタン）でのドラッグで範囲指定枠を表示し、離した時点で枠に重なる
//   オブジェクトをまとめて選択する。
// - しきい値（MARQUEE_THRESHOLD_PX）を超えて初めて範囲選択を「アクティブ化」する。
//   それ未満の移動は「ただの右クリック」とみなし、選択も枠表示もせず、右クリック
//   メニュー（コンテキストメニュー）を従来どおり表示させる。
// - Ctrl / Meta / Shift を押しながら離すと既存の選択へ追加（和集合）、押していない
//   ときは選択を置換する。
// - 範囲選択ドラッグが成立した直後の contextmenu イベントは抑止し、枠選択操作が
//   ブラウザ標準メニューやオブジェクトの右クリックメニューに化けないようにする。
//
// 左ボタンのパン / オブジェクトドラッグ / ペン描画 / 配置とは排他で、それらは
// use-pointer-mode / use-object-drag 側が主ボタン（button === 0）のみで処理する。
//
// 既知の制約（主たる対象環境は Windows + マウスで、そこでは下記いずれも問題ない）:
// - contextmenu の抑止は「contextmenu が pointerup 後に発火する」前提（Windows / 多くの
//   ブラウザ）に依存する。WebKit/Safari など contextmenu を pointerdown 起点で発火する
//   実装では、右ドラッグ開始時に標準メニューが一瞬出ることがある。pointerdown 時点では
//   クリックかドラッグか判別できないため、ここで一律抑止すると通常の右クリックメニューを
//   壊す。トレードオフとして「通常の右クリックメニューを優先」する設計とする。
// - secondary click 判定は button === 2 を用いる。macOS の Control+クリックは WebKit が
//   button === 0 を返す既知差分があり範囲選択の起点にはならない（button 0 + Ctrl は複数
//   選択トグルに割り当て済みのため流用できない）。macOS では実右ボタン / トラックパッドの
//   副ボタンクリック（button === 2）で範囲選択を行う。

import { useEffect, useRef, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { screenToStage } from "../../../lib/canvas-coordinates";
import { normalizeMarqueeRect, objectIdsInMarquee } from "../../../lib/marquee";

// 右ボタンドラッグがこの距離（クライアント px）を超えたら範囲選択として扱う。
const MARQUEE_THRESHOLD_PX = 4;
// マウスの副ボタン（右クリック）。
const SECONDARY_BUTTON = 2;

interface MarqueeDragState {
  readonly pointerId: number;
  readonly startClientX: number;
  readonly startClientY: number;
  readonly startStageX: number;
  readonly startStageY: number;
  /** しきい値を超えて範囲選択がアクティブ化したか。 */
  active: boolean;
}

export function useMarqueeSelection(containerRef: RefObject<HTMLElement | null>): void {
  const dragStateRef = useRef<MarqueeDragState | null>(null);
  // 範囲選択ドラッグ直後の contextmenu を 1 回だけ抑止するためのフラグ。
  const suppressContextMenuRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const onPointerDown = (event: PointerEvent): void => {
      // 新しいジェスチャの開始時にだけ、前のジェスチャで立てた抑止フラグをリセットする。
      // （右ボタンドラッグ後に普通の右クリックをしたとき、メニューが出ないままになる退行を防ぐ。）
      // ただし進行中の右ドラッグ（dragStateRef あり）の最中に別ポインタが down しても、
      // そのドラッグの抑止フラグを巻き込んで落とさないようにする（マルチポインタ対策）。
      if (!dragStateRef.current) suppressContextMenuRef.current = false;
      // 右ボタン以外は範囲選択を開始しない。
      if (event.button !== SECONDARY_BUTTON) return;
      const view = useCanvasStore.getState().view;
      const start = screenToStage({ x: event.clientX, y: event.clientY }, view);
      dragStateRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startStageX: start.x,
        startStageY: start.y,
        active: false,
      };
      // ここでは pointer capture しない。capture するとブラウザによっては後続の
      // contextmenu の target が capture 先（container）へ寄り、しきい値未満の通常の
      // 右クリックでもオブジェクトの右クリックメニュー（onContextMenuCapture）が
      // 開かなくなる。capture は実際にドラッグが成立した時点（onPointerMove）で行う。
    };

    const onPointerMove = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      const distance = Math.hypot(
        event.clientX - dragState.startClientX,
        event.clientY - dragState.startClientY
      );
      if (!dragState.active) {
        // しきい値未満の手ブレでは枠を出さない（ただの右クリックを保護する）。
        if (distance <= MARQUEE_THRESHOLD_PX) return;
        dragState.active = true;
        // ドラッグが成立した時点で、直後の contextmenu を抑止対象にする。
        suppressContextMenuRef.current = true;
        // この時点で初めて capture し、ポインタが container 外や別要素へ移っても
        // move を取りこぼさないようにする（通常の右クリックでは capture しない）。
        if (!container.hasPointerCapture(dragState.pointerId)) {
          container.setPointerCapture(dragState.pointerId);
        }
      }
      const view = useCanvasStore.getState().view;
      const current = screenToStage({ x: event.clientX, y: event.clientY }, view);
      useCanvasStore
        .getState()
        .setMarqueeRect(
          normalizeMarqueeRect(dragState.startStageX, dragState.startStageY, current.x, current.y)
        );
    };

    const onPointerUp = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      dragStateRef.current = null;
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
      // アクティブ化していない＝ただの右クリック。枠選択せずメニューはそのまま出させる。
      if (!dragState.active) return;

      const view = useCanvasStore.getState().view;
      const current = screenToStage({ x: event.clientX, y: event.clientY }, view);
      const rect = normalizeMarqueeRect(
        dragState.startStageX,
        dragState.startStageY,
        current.x,
        current.y
      );
      useCanvasStore.getState().setMarqueeRect(null);
      const objects = useCanvasDocStore.getState().objects;
      const hitIds = objectIdsInMarquee(objects, rect);
      // 修飾キー併用時は既存選択へ追加（和集合）、それ以外は置換する。
      const additive = event.ctrlKey || event.metaKey || event.shiftKey;
      if (additive) {
        const merged = new Set(useCanvasStore.getState().selectedObjectIds);
        for (const id of hitIds) merged.add(id);
        useCanvasStore.getState().setSelectedObjectIds(Array.from(merged));
      } else {
        useCanvasStore.getState().setSelectedObjectIds(hitIds);
      }
    };

    const onPointerCancel = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      dragStateRef.current = null;
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
      // 中断されたジェスチャには後続の contextmenu が無いため、抑止フラグを残さない。
      // 残すと、次にキーボード（Shift+F10 / メニューキー）で開く正当なコンテキスト
      // メニューを 1 回だけ誤って抑止してしまう。
      suppressContextMenuRef.current = false;
      useCanvasStore.getState().setMarqueeRect(null);
    };

    // 範囲選択ドラッグ直後の contextmenu を抑止する。window の capture フェーズで処理し、
    // ブラウザ標準メニューと、React がルートで拾うオブジェクトの右クリックメニューの
    // 両方より先に preventDefault / stopPropagation する。
    const onContextMenu = (event: MouseEvent): void => {
      if (!suppressContextMenuRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      suppressContextMenuRef.current = false;
    };

    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", onPointerUp);
    container.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("contextmenu", onContextMenu, { capture: true });
    return () => {
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("contextmenu", onContextMenu, { capture: true });
    };
  }, [containerRef]);
}
